// Tab system for the exotics pricer page. Mirrors the OTC trader tab
// pattern (otc-trader-provider.tsx) but scoped to exotics.
//
// Two tab kinds:
//   - MULTI_TOKEN (default, always present, not deletable) — current page
//     behaviour: each column picks its own token pair.
//   - Custom (EXOTICS-TAB_<token>_<label>) — token pair is LOCKED at the
//     tab level. Every column added inside this tab inherits the token,
//     enabling structure-building (straddles, strangles, butterflies) on
//     a consistent underlying.
//
// State is persisted via usePersistentState so user's tab list survives
// reloads.
import { ReactNode, createContext, useCallback } from 'react';

import usePersistentState from '../use-persistent-state';
import { Token } from '../../pages/exotics-pricer/contracts';

export const EXOTICS_TAB_PREFIX = 'EXOTICS-TAB_';
export const MULTI_TOKEN_TAB_KEY = 'MULTI_TOKEN';

export interface ExoticsTab {
  key: string;
  label: string;
  // Locked token carried as METADATA rather than encoded in the key. Used by
  // the single reusable Market Probabilities handoff tab (IM-1955), whose token
  // must be able to change (re-lock to whichever token was last clicked)
  // WITHOUT a per-token key. When set it takes precedence over the key-derived
  // locked token; ordinary custom tabs leave it undefined and lock via the key.
  lockedToken?: string;
}

const DEFAULT_TABS: ExoticsTab[] = [{ key: MULTI_TOKEN_TAB_KEY, label: 'Multi-token Exotics' }];

// Encode / decode the locked token from a custom tab key.
//   EXOTICS-TAB_BTC_My Spread  →  token='BTC', label='My Spread'
export const buildCustomTabKey = (token: Token, label: string): string =>
  `${EXOTICS_TAB_PREFIX}${token}|${label}`;

export const isCustomTabKey = (key: string): boolean => key.startsWith(EXOTICS_TAB_PREFIX);

// PER-TOKEN Market-Probabilities handoff tabs (IM-1955). One screen per token
// so an XRP basket and a BTC basket can be open at once, each accumulating its
// own rows. The KEY + LABEL carry the DISPLAY token (e.g. 'XRP') so the tab
// reads "Market Probabilities (XRP)"; the tab LOCKS to the registry base_quote
// (e.g. 'XRP_USDC') via tab.lockedToken metadata — display and lock are
// intentionally separate. The key is NOT an EXOTICS_TAB_PREFIX custom key, so
// the tab bar shows the plain label with no token badge / ellipsis menu.
export const MP_HANDOFF_KEY_PREFIX = 'MP_HANDOFF_';
export const mpHandoffKey = (displayToken: string): string =>
  `${MP_HANDOFF_KEY_PREFIX}${displayToken}`;
export const mpHandoffLabel = (displayToken: string): string =>
  `Market Probabilities (${displayToken})`;
export const isMpHandoffKey = (key: string): boolean => key.startsWith(MP_HANDOFF_KEY_PREFIX);

// Custom tab key format: `${EXOTICS_TAB_PREFIX}${baseQuote}|${label}`.
// We use '|' as the separator now because base_quote itself contains '_'
// for alt-coin pairs (e.g. 'SOL_USDC', 'TRUMP_USDT'). The old '_' split
// confused 'SOL_USDC_My Tab' with token='SOL', label='USDC_My Tab'.
// Older tabs still parse via the legacy underscore path for backwards
// compat — BTC and ETH are the only single-segment tokens that ever
// existed before this refactor, so a key without '|' implies legacy.
export const lockedTokenFromTabKey = (key: string): Token | null => {
  if (!isCustomTabKey(key)) return null;
  const rest = key.slice(EXOTICS_TAB_PREFIX.length);
  const pipe = rest.indexOf('|');
  if (pipe !== -1) return rest.slice(0, pipe);
  // Legacy: '${token}_${label}' where token was 'BTC' or 'ETH' only.
  const us = rest.indexOf('_');
  return us === -1 ? rest : rest.slice(0, us);
};

export const labelFromTabKey = (key: string, fallback: string): string => {
  if (!isCustomTabKey(key)) return fallback;
  const rest = key.slice(EXOTICS_TAB_PREFIX.length);
  const pipe = rest.indexOf('|');
  if (pipe !== -1) return rest.slice(pipe + 1);
  // Legacy underscore separator.
  const us = rest.indexOf('_');
  return us === -1 ? fallback : rest.slice(us + 1);
};

interface ExoticsTabsContextType {
  tabs: ExoticsTab[];
  currentTab: string;
  setCurrentTab: (key: string) => void;
  setTabs: (tabs: ExoticsTab[]) => void;
  addTab: (token: Token, label: string) => string;
  // Upsert this token's Market Probabilities handoff tab (keyed/labelled by the
  // DISPLAY token, locked to the base_quote via metadata) and select it. Reuses
  // the token's existing screen if present.
  openHandoffTab: (displayToken: string, baseQuote: Token) => void;
  renameTab: (key: string, newLabel: string) => void;
  deleteTab: (key: string) => void;
  duplicateTab: (key: string, newLabel: string) => string | null;
}

export const ExoticsTabsContext = createContext<ExoticsTabsContextType>(
  {} as ExoticsTabsContextType,
);

export const ExoticsTabsProvider = ({ children }: { children: ReactNode }) => {
  const STATE_PATH = ['EXOTICS-PRICER'];

  const [tabs, setTabsRaw] = usePersistentState<ExoticsTab[]>(STATE_PATH, 'tabs', DEFAULT_TABS);
  const [currentTab, setCurrentTabRaw] = usePersistentState<string>(
    STATE_PATH,
    'currentTab',
    MULTI_TOKEN_TAB_KEY,
  );

  const setTabs = useCallback((next: ExoticsTab[]) => setTabsRaw(next), [setTabsRaw]);

  const setCurrentTab = useCallback((key: string) => setCurrentTabRaw(key), [setCurrentTabRaw]);

  const addTab = useCallback(
    (token: Token, label: string): string => {
      const key = buildCustomTabKey(token, label);
      setTabsRaw((prev) => {
        if (prev.some((t) => t.key === key)) return prev;
        return [...prev, { key, label }];
      });
      setCurrentTabRaw(key);
      return key;
    },
    [setTabsRaw, setCurrentTabRaw],
  );

  const openHandoffTab = useCallback(
    (displayToken: string, baseQuote: Token) => {
      const key = mpHandoffKey(displayToken);
      setTabsRaw((prev) => {
        const existing = prev.find((t) => t.key === key);
        if (!existing) {
          return [...prev, { key, label: mpHandoffLabel(displayToken), lockedToken: baseQuote }];
        }
        // Reuse the token's tab; refresh the lock only if base_quote resolution
        // changed, so an unchanged re-click doesn't churn the array identity.
        if (existing.lockedToken === baseQuote) return prev;
        return prev.map((t) => (t.key === key ? { ...t, lockedToken: baseQuote } : t));
      });
      setCurrentTabRaw(key);
    },
    [setTabsRaw, setCurrentTabRaw],
  );

  const renameTab = useCallback(
    (key: string, newLabel: string) => {
      setTabsRaw((prev) => prev.map((t) => (t.key === key ? { ...t, label: newLabel } : t)));
    },
    [setTabsRaw],
  );

  const deleteTab = useCallback(
    (key: string) => {
      // Multi-token tab is permanent — guard against accidental delete.
      if (key === MULTI_TOKEN_TAB_KEY) return;
      setTabsRaw((prev) => prev.filter((t) => t.key !== key));
      setCurrentTabRaw((cur) => (cur === key ? MULTI_TOKEN_TAB_KEY : cur));
    },
    [setTabsRaw, setCurrentTabRaw],
  );

  const duplicateTab = useCallback(
    (sourceKey: string, newLabel: string): string | null => {
      const src = tabs.find((t) => t.key === sourceKey);
      if (!src) return null;
      // Source must be a custom tab to duplicate (multi-token doesn't have
      // a token to inherit).
      const tok = lockedTokenFromTabKey(sourceKey);
      if (!tok) return null;
      const newKey = buildCustomTabKey(tok, newLabel);
      setTabsRaw((prev) => {
        if (prev.some((t) => t.key === newKey)) return prev;
        return [...prev, { key: newKey, label: newLabel }];
      });
      setCurrentTabRaw(newKey);
      return newKey;
    },
    [tabs, setTabsRaw, setCurrentTabRaw],
  );

  return (
    <ExoticsTabsContext.Provider
      value={{
        tabs,
        currentTab,
        setCurrentTab,
        setTabs,
        addTab,
        openHandoffTab,
        renameTab,
        deleteTab,
        duplicateTab,
      }}
    >
      {children}
    </ExoticsTabsContext.Provider>
  );
};
